import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '../../hooks/useScrollAnimation';

export default function BlogPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTag, setActiveTag] = useState('');
  const [showTopics, setShowTopics] = useState(false);

  const heroAnim = useScrollAnimation({ threshold: 0.05 });
  const articlesAnim = useStaggerAnimation(8, { threshold: 0.05 });
  const sidebarAnim = useScrollAnimation({ threshold: 0.1 });

  const featuredPost = {
    id: 1,
    title: 'Complete Guide to Organic Pest Management for Cannabis',
    category: 'Growing Tips',
    excerpt: 'Learn natural methods to protect your cannabis plants from common pests without harmful chemicals. Discover integrated pest management strategies used by expert growers worldwide.',
    author: 'H Boss',
    authorRole: 'Master Grower',
    date: 'January 15, 2025',
    readTime: '8 min read',
    image: 'https://public.readdy.ai/ai/img_res/80888e6e20d47f86f6b57dd3749f1848.jpg'
  };

  const blogPosts = [
    {
      id: 2,
      title: 'Sustainable Growing: Worm Composting Basics',
      category: 'Sustainability',
      excerpt: 'Discover how worm cultivation creates the perfect organic fertilizer for thriving cannabis plants. Learn the science behind vermicomposting.',
      author: 'HR Boss',
      date: 'January 12, 2025',
      readTime: '6 min read',
      image: 'https://readdy.ai/api/search-image?query=rich%20dark%20compost%20soil%20with%20earthworms%20in%20wooden%20composting%20bin%20surrounded%20by%20green%20plants%20natural%20outdoor%20setting%20organic%20farming%20sustainable%20agriculture%20educational%20photography%20earthy%20natural%20tones&width=600&height=400&seq=blog002&orientation=landscape'
    },
    {
      id: 3,
      title: 'Beginner\'s Guide to Cannabis Cultivation',
      category: 'Beginner Guides',
      excerpt: 'Everything you need to know to start your first cannabis grow successfully from seed to harvest. Step-by-step instructions for new growers.',
      author: 'A Boss',
      date: 'January 10, 2025',
      readTime: '10 min read',
      image: 'https://readdy.ai/api/search-image?query=young%20cannabis%20seedlings%20sprouting%20in%20organic%20soil%20pots%20on%20wooden%20table%20with%20gardening%20tools%20bright%20natural%20light%20beginner%20friendly%20setup%20clean%20educational%20photography%20sustainable%20growing%20aesthetic&width=600&height=400&seq=blog003&orientation=landscape'
    },
    {
      id: 4,
      title: 'Understanding Cannabis Nutrients: NPK Ratios Explained',
      category: 'Growing Tips',
      excerpt: 'Master the art of feeding your plants with this comprehensive guide to nitrogen, phosphorus, and potassium ratios throughout growth stages.',
      author: 'H Boss',
      date: 'January 8, 2025',
      readTime: '7 min read',
      image: 'https://readdy.ai/api/search-image?query=organic%20plant%20nutrients%20bottles%20and%20measuring%20tools%20on%20clean%20white%20surface%20with%20healthy%20green%20cannabis%20leaves%20professional%20product%20photography%20educational%20content%20natural%20farming%20supplies%20minimalist%20aesthetic&width=600&height=400&seq=blog004&orientation=landscape'
    },
    {
      id: 5,
      title: 'Indoor vs Outdoor Growing: Pros and Cons',
      category: 'Beginner Guides',
      excerpt: 'Compare indoor and outdoor cultivation methods to determine which approach best suits your goals, space, and budget.',
      author: 'HR Boss',
      date: 'January 5, 2025',
      readTime: '9 min read',
      image: 'https://readdy.ai/api/search-image?query=split%20screen%20comparison%20indoor%20cannabis%20grow%20tent%20with%20LED%20lights%20versus%20outdoor%20cannabis%20plants%20in%20natural%20sunlight%20professional%20photography%20educational%20content%20clean%20composition%20sustainable%20cultivation&width=600&height=400&seq=blog005&orientation=landscape'
    },
    {
      id: 6,
      title: 'The Science of CBD: Benefits and Uses',
      category: 'Wellness',
      excerpt: 'Explore the therapeutic properties of CBD and how it interacts with the human endocannabinoid system for natural wellness.',
      author: 'A Boss',
      date: 'January 3, 2025',
      readTime: '8 min read',
      image: 'https://readdy.ai/api/search-image?query=CBD%20oil%20dropper%20with%20molecular%20structure%20diagram%20and%20green%20cannabis%20leaves%20on%20clean%20white%20background%20scientific%20educational%20content%20professional%20photography%20wellness%20health%20aesthetic%20minimalist%20composition&width=600&height=400&seq=blog006&orientation=landscape'
    },
    {
      id: 7,
      title: 'Harvesting and Curing: Timing is Everything',
      category: 'Growing Tips',
      excerpt: 'Learn when and how to harvest your cannabis plants for maximum potency and flavor. Master the art of proper drying and curing.',
      author: 'H Boss',
      date: 'December 30, 2024',
      readTime: '11 min read',
      image: 'https://readdy.ai/api/search-image?query=cannabis%20buds%20hanging%20to%20dry%20in%20controlled%20environment%20with%20humidity%20monitor%20professional%20harvest%20setup%20natural%20lighting%20educational%20photography%20organic%20cultivation%20clean%20professional%20aesthetic&width=600&height=400&seq=blog007&orientation=landscape'
    },
    {
      id: 8,
      title: 'Water Quality and pH Management',
      category: 'Growing Tips',
      excerpt: 'Understanding water chemistry is crucial for healthy plants. Learn how to test and adjust pH levels for optimal nutrient absorption.',
      author: 'HR Boss',
      date: 'December 28, 2024',
      readTime: '6 min read',
      image: 'https://readdy.ai/api/search-image?query=pH%20testing%20equipment%20and%20water%20quality%20meters%20with%20cannabis%20plant%20in%20background%20clean%20white%20surface%20professional%20photography%20educational%20content%20scientific%20growing%20tools%20minimalist%20aesthetic&width=600&height=400&seq=blog008&orientation=landscape'
    },
    {
      id: 9,
      title: 'Building Your First Grow Room',
      category: 'Beginner Guides',
      excerpt: 'A complete guide to setting up an efficient indoor grow space. From ventilation to lighting, we cover all the essentials.',
      author: 'A Boss',
      date: 'December 25, 2024',
      readTime: '12 min read',
      image: 'https://readdy.ai/api/search-image?query=modern%20indoor%20cannabis%20grow%20room%20setup%20with%20LED%20lights%20ventilation%20system%20and%20healthy%20plants%20professional%20photography%20clean%20organized%20space%20educational%20content%20high%20tech%20cultivation%20environment&width=600&height=400&seq=blog009&orientation=landscape'
    }
  ];

  const popularTopics = [
    'Organic Growing', 'Pest Control', 'CBD Benefits',
    'Indoor Cultivation', 'Nutrients', 'Harvesting',
    'Beginner Tips', 'Sustainability'
  ];

  const recentPosts = blogPosts.slice(0, 5);

  return (
    <div className="min-h-screen bg-white pt-20 overflow-x-hidden">

      {/* Featured Hero */}
      <section
        ref={heroAnim.ref as React.RefObject<HTMLElement>}
        className="relative w-full h-[400px] md:h-[600px] mb-8 md:mb-16 overflow-hidden"
        style={{
          opacity: heroAnim.isVisible ? 1 : 0,
          transition: 'opacity 0.9s ease-out'
        }}
      >
        <img
          src={featuredPost.image}
          alt={featuredPost.title}
          className="w-full h-full object-cover object-top scale-105 hover:scale-100 transition-transform duration-[8s]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 pb-8 md:pb-16 w-full">
            <div
              className="max-w-3xl"
              style={{
                opacity: heroAnim.isVisible ? 1 : 0,
                transform: heroAnim.isVisible ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.9s ease-out 0.3s, transform 0.9s ease-out 0.3s'
              }}
            >
              <span className="inline-block px-3 md:px-4 py-1 md:py-2 bg-white/90 text-[#2D5F3F] text-xs md:text-sm font-bold rounded-full mb-3 md:mb-4">
                {featuredPost.category}
              </span>
              <h1 className="text-2xl md:text-5xl font-bold text-white mb-3 md:mb-4 leading-tight hover:text-[#D4AF37] transition-colors duration-300 cursor-default">
                {featuredPost.title}
              </h1>
              <p className="text-base md:text-xl text-white/90 mb-4 md:mb-6 leading-relaxed line-clamp-2 md:line-clamp-none">
                {featuredPost.excerpt}
              </p>
              <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#D4AF37] flex items-center justify-center text-white font-bold text-lg md:text-xl hover:scale-110 transition-transform duration-300 cursor-pointer">
                  {featuredPost.author.charAt(0)}
                </div>
                <div className="text-white">
                  <div className="font-semibold text-base md:text-lg">{featuredPost.author}</div>
                  <div className="text-xs md:text-sm text-white/80">
                    {featuredPost.authorRole} · {featuredPost.date} · {featuredPost.readTime}
                  </div>
                </div>
              </div>
              <Link
                to="/blog"
                className="group inline-flex items-center gap-2 md:gap-3 bg-[#2D5F3F] text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold hover:bg-[#D4AF37] hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl text-sm md:text-base"
              >
                Read Full Article
                <i className="ri-arrow-right-line text-lg md:text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-6 md:py-12">
        <div className="flex flex-col lg:flex-row gap-8 md:gap-12">

          {/* Main Articles */}
          <main className="flex-1">
            <h2
              className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-6 md:mb-8 transition-all duration-700"
              style={{
                opacity: articlesAnim.visibleItems[0] ? 1 : 0,
                transform: articlesAnim.visibleItems[0] ? 'translateX(0)' : 'translateX(-30px)'
              }}
            >
              Latest Articles
            </h2>

            {/* Mobile Topics Toggle */}
            <div className="lg:hidden mb-6">
              <button
                onClick={() => setShowTopics(!showTopics)}
                className="w-full bg-white rounded-xl shadow-lg p-4 flex items-center justify-between cursor-pointer hover:shadow-xl transition-shadow duration-300"
              >
                <span className="text-lg font-bold text-[#2C2C2C]">Popular Topics</span>
                <i className={`ri-arrow-${showTopics ? 'up' : 'down'}-s-line text-2xl text-[#2D5F3F] transition-transform duration-300`}></i>
              </button>
              {showTopics && (
                <div className="bg-white rounded-xl shadow-lg p-4 mt-2">
                  <div className="flex flex-wrap gap-2">
                    {popularTopics.map((topic) => (
                      <button
                        key={topic}
                        onClick={() => setActiveTag(activeTag === topic ? '' : topic)}
                        className={`px-4 py-2 text-sm font-semibold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105 active:scale-95 ${
                          activeTag === topic
                            ? 'bg-[#2D5F3F] text-white shadow-md'
                            : 'bg-[#F8FAF9] text-[#2D5F3F] hover:bg-[#2D5F3F] hover:text-white'
                        }`}
                      >
                        {topic}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div
              ref={articlesAnim.ref as React.RefObject<HTMLDivElement>}
              className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12"
            >
              {blogPosts.map((post, index) => (
                <Link
                  key={post.id}
                  to="/blog"
                  className={`group cursor-pointer ${
                    index % 3 === 0 ? 'bg-white' : 'bg-[#F8FAF9]'
                  } rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-400 hover:-translate-y-2`}
                  style={{
                    opacity: articlesAnim.visibleItems[index] ? 1 : 0,
                    transform: articlesAnim.visibleItems[index]
                      ? 'translateY(0) scale(1)'
                      : 'translateY(40px) scale(0.97)',
                    transition: 'opacity 0.55s ease-out, transform 0.55s ease-out, box-shadow 0.3s'
                  }}
                >
                  <div className="relative w-full h-56 md:h-64 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-600"
                    />
                    <span className="absolute top-3 md:top-4 left-3 md:left-4 px-3 py-1 bg-white/90 text-[#2D5F3F] text-xs font-bold rounded-full shadow-sm">
                      {post.category}
                    </span>
                    {/* Read time badge */}
                    <span className="absolute bottom-3 md:bottom-4 right-3 md:right-4 px-3 py-1 bg-black/60 text-white text-xs rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {post.readTime}
                    </span>
                  </div>
                  <div className="p-5 md:p-6">
                    <h3 className="text-xl md:text-2xl font-bold text-[#2C2C2C] mb-2 md:mb-3 line-clamp-2 group-hover:text-[#2D5F3F] transition-colors duration-300 leading-tight">
                      {post.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4 line-clamp-3 leading-relaxed">
                      {post.excerpt}
                    </p>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-sm text-gray-500 gap-2">
                      <span className="font-semibold group-hover:text-[#2D5F3F] transition-colors duration-300">{post.author}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs md:text-sm">{post.date}</span>
                        <span className="w-1 h-1 rounded-full bg-gray-400 inline-block"></span>
                        <span className="flex items-center gap-1 text-xs md:text-sm">
                          <i className="ri-time-line text-[#D4AF37]"></i>
                          {post.readTime}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="flex justify-center">
              <button className="group bg-[#2D5F3F] text-white px-8 md:px-12 py-3 md:py-4 rounded-full font-semibold hover:bg-[#1A3A2A] hover:scale-105 active:scale-95 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-3 shadow-md hover:shadow-xl text-sm md:text-base">
                Load More Articles
                <i className="ri-refresh-line group-hover:rotate-180 transition-transform duration-500"></i>
              </button>
            </div>
          </main>

          {/* Sidebar - Desktop Only */}
          <aside
            ref={sidebarAnim.ref as React.RefObject<HTMLElement>}
            className="hidden lg:block w-80 flex-shrink-0"
            style={{
              opacity: sidebarAnim.isVisible ? 1 : 0,
              transform: sidebarAnim.isVisible ? 'translateX(0)' : 'translateX(30px)',
              transition: 'opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s'
            }}
          >
            <div className="sticky top-24 space-y-8">

              {/* Search */}
              <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search articles..."
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2D5F3F] focus:border-transparent transition-all duration-300"
                  />
                  <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl"></i>
                </div>
              </div>

              {/* Popular Topics */}
              <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-xl font-bold text-[#2C2C2C] mb-4">Popular Topics</h3>
                <div className="flex flex-wrap gap-2">
                  {popularTopics.map((topic) => (
                    <button
                      key={topic}
                      onClick={() => setActiveTag(activeTag === topic ? '' : topic)}
                      className={`px-4 py-2 text-sm font-semibold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105 active:scale-95 ${
                        activeTag === topic
                          ? 'bg-[#2D5F3F] text-white shadow-md'
                          : 'bg-[#F8FAF9] text-[#2D5F3F] hover:bg-[#2D5F3F] hover:text-white'
                      }`}
                    >
                      {topic}
                    </button>
                  ))}
                </div>
              </div>

              {/* Recent Posts */}
              <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-xl font-bold text-[#2C2C2C] mb-4">Recent Posts</h3>
                <div className="space-y-4">
                  {recentPosts.map((post, i) => (
                    <Link
                      key={post.id}
                      to="/blog"
                      className="flex gap-3 group cursor-pointer"
                      style={{
                        opacity: sidebarAnim.isVisible ? 1 : 0,
                        transform: sidebarAnim.isVisible ? 'translateX(0)' : 'translateX(20px)',
                        transition: `opacity 0.5s ease-out ${0.3 + i * 0.1}s, transform 0.5s ease-out ${0.3 + i * 0.1}s`
                      }}
                    >
                      <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-400"
                        />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-[#2C2C2C] line-clamp-2 group-hover:text-[#2D5F3F] transition-colors duration-300 mb-1">
                          {post.title}
                        </h4>
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <i className="ri-calendar-line text-[#D4AF37]"></i>
                          {post.date}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Newsletter Widget */}
              <div className="bg-gradient-to-br from-[#2D5F3F] to-[#1A3A2A] rounded-xl shadow-lg p-6 text-center hover:shadow-2xl transition-shadow duration-300">
                <i className="ri-mail-line text-5xl text-[#D4AF37] mb-4 block animate-pulse"></i>
                <h3 className="text-xl font-bold text-white mb-3">Stay Updated</h3>
                <p className="text-sm text-white/90 mb-4">
                  Get the latest growing tips and wellness insights delivered to your inbox.
                </p>
                <input
                  type="email"
                  placeholder="Your email"
                  className="w-full px-4 py-3 rounded-lg text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] transition-all duration-300"
                />
                <button className="w-full bg-[#D4AF37] text-[#1A3A2A] py-3 rounded-lg font-semibold hover:bg-[#F5F1E8] hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 cursor-pointer whitespace-nowrap">
                  Subscribe
                </button>
              </div>

              {/* Affiliate Banner */}
              <div className="flex flex-col items-center gap-3">
                <p className="text-xs text-gray-400 uppercase tracking-widest font-semibold">Our Partners</p>
                <a
                  href="https://www.cannatop100.com?a_aid=ElixirSue&a_bid=ebcdd1a5"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block hover:opacity-80 transition-opacity duration-300 cursor-pointer"
                >
                  <img
                    src="https://www.greenaffiliates.com/accounts/default1/nxkkkeyb/ebcdd1a5.png"
                    alt="CannaTOP 100 - Top Cannabis Sites"
                    title="CannaTOP 100"
                    width={300}
                    height={90}
                    className="mx-auto rounded-lg shadow-md w-full"
                  />
                </a>
                <img
                  alt=""
                  src="https://www.greenaffiliates.com/scripts/nxkkkeyi?a_aid=ElixirSue&a_bid=ebcdd1a5"
                  width={1}
                  height={1}
                />
              </div>

            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
